import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isSameDay,
  startOfDay,
  endOfDay,
  subDays,
  subWeeks,
  subMonths,
} from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { utils, writeFile } from "xlsx";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

// Cell Components
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value}
  </MDTypography>
);
SerialCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const MemberCell = ({ value, row, onClick }) => (
  <MDBox
    display="flex"
    alignItems="center"
    lineHeight={1}
    onClick={() => onClick(row.original)}
    sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
  >
    <MDAvatar name={value} size="sm" />
    <MDBox ml={2} lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {value}
      </MDTypography>
      <MDTypography variant="caption">{row.original.nationality || "Member"}</MDTypography>
    </MDBox>
  </MDBox>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      nationality: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const AttendanceStatusCell = ({ value }) => (
  <MDBox display="flex" justifyContent="center">
    {value ? (
      <CheckIcon color="success" fontSize="medium" />
    ) : (
      <CloseIcon color="error" fontSize="medium" />
    )}
  </MDBox>
);
AttendanceStatusCell.propTypes = {
  value: PropTypes.bool.isRequired,
};

const DateTimeCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value === "Not checked in" || value === "Absent" ? value : new Date(value).toLocaleString()}
  </MDTypography>
);
DateTimeCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const MethodCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "biometric" ? "success" : "info"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
MethodCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const PhoneCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value || "N/A"}
  </MDTypography>
);
PhoneCell.propTypes = {
  value: PropTypes.string,
};

const EmailCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value || "N/A"}
  </MDTypography>
);
EmailCell.propTypes = {
  value: PropTypes.string,
};

// Member Details Modal Component
const MemberDetailsModal = ({ member, open, onClose, churchId }) => {
  const [attendanceData, setAttendanceData] = useState({
    week: [],
    month: [],
    all: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open && member) {
      fetchMemberAttendance();
    }
  }, [open, member]);

  const fetchMemberAttendance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      // Fetch attendance for different time periods
      const periods = [
        {
          key: "week",
          start: format(subWeeks(new Date(), 1), "yyyy-MM-dd"),
          end: format(new Date(), "yyyy-MM-dd"),
        },
        {
          key: "month",
          start: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
          end: format(new Date(), "yyyy-MM-dd"),
        },
        {
          key: "all",
          start: null,
          end: null,
        },
      ];

      const attendanceData = { week: [], month: [], all: [] };

      for (const period of periods) {
        let url = `https://api.ofi-cia.com/api/attendance/report/church/${churchId}/member/${member.id}`;

        if (period.start && period.end) {
          url += `?start_date=${period.start}&end_date=${period.end}`;
        }

        const response = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          attendanceData[period.key] = data;
        }
      }

      setAttendanceData(attendanceData);
    } catch (error) {
      console.error("Failed to fetch member attendance:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load member attendance details",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderAttendanceTable = (data) => {
    if (loading) {
      return <MDTypography>Loading attendance data...</MDTypography>;
    }

    if (!data || data.length === 0) {
      return <MDTypography>No attendance records found.</MDTypography>;
    }

    return (
      <MDBox>
        <MDTypography variant="h6" gutterBottom>
          {data.length} Attendance Record{data.length !== 1 ? "s" : ""}
        </MDTypography>
        <MDBox sx={{ maxHeight: "300px", overflow: "auto" }}>
          {data.map((record, index) => (
            <MDBox
              key={index}
              mb={2}
              p={1}
              sx={{ border: "1px solid #e0e0e0", borderRadius: "4px" }}
            >
              <MDTypography variant="caption" display="block">
                <strong>Date:</strong> {new Date(record.checked_in_at).toLocaleDateString()}
              </MDTypography>
              <MDTypography variant="caption" display="block">
                <strong>Time:</strong> {new Date(record.checked_in_at).toLocaleTimeString()}
              </MDTypography>
              <MDTypography variant="caption" display="block">
                <strong>Method:</strong> {record.method || "N/A"}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    );
  };

  if (!member) return null;

  return (
    <MDBox
      display={open ? "block" : "none"}
      position="fixed"
      top={0}
      left={0}
      width="100%"
      height="100%"
      bgcolor="rgba(0,0,0,0.5)" // This is the semi-transparent overlay
      zIndex={9999}
      onClick={onClose}
    >
      <MDBox
        position="absolute"
        top="50%"
        left="50%"
        width="90%"
        maxWidth="600px"
        maxHeight="80vh"
        bgcolor="background.paper"
        borderRadius="8px"
        boxShadow={24}
        p={4}
        sx={{
          transform: "translate(-50%, -50%)",
          overflow: "auto",
          backgroundColor: (theme) => theme.palette.background.paper,
          boxShadow: (theme) => theme.shadows[5],
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <MDTypography variant="h5">Member Attendance Details</MDTypography>
          <CloseIcon onClick={onClose} sx={{ cursor: "pointer" }} />
        </MDBox>

        <MDBox display="flex" alignItems="center" mb={3}>
          <MDAvatar name={member.memberName} size="lg" />
          <MDBox ml={2}>
            <MDTypography variant="h6">{member.memberName}</MDTypography>
            <MDTypography variant="body2" color="textSecondary">
              {member.phone || "No phone number"} | {member.email || "No email"}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox borderBottom={1} borderColor="divider" mb={2}>
          <MDBox sx={{ borderBottom: 1, borderColor: "divider" }}>
            <MDBox display="flex">
              <MDTypography
                variant="button"
                sx={{
                  mr: 2,
                  cursor: "pointer",
                  borderBottom: activeTab === 0 ? "2px solid" : "none",
                  pb: 1,
                  color: activeTab === 0 ? "primary.main" : "text.secondary",
                }}
                onClick={() => setActiveTab(0)}
              >
                Past Week
              </MDTypography>
              <MDTypography
                variant="button"
                sx={{
                  mr: 2,
                  cursor: "pointer",
                  borderBottom: activeTab === 1 ? "2px solid" : "none",
                  pb: 1,
                  color: activeTab === 1 ? "primary.main" : "text.secondary",
                }}
                onClick={() => setActiveTab(1)}
              >
                Past Month
              </MDTypography>
              <MDTypography
                variant="button"
                sx={{
                  cursor: "pointer",
                  borderBottom: activeTab === 2 ? "2px solid" : "none",
                  pb: 1,
                  color: activeTab === 2 ? "primary.main" : "text.secondary",
                }}
                onClick={() => setActiveTab(2)}
              >
                All Time
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox mt={2}>
          {activeTab === 0 && renderAttendanceTable(attendanceData.week)}
          {activeTab === 1 && renderAttendanceTable(attendanceData.month)}
          {activeTab === 2 && renderAttendanceTable(attendanceData.all)}
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

MemberDetailsModal.propTypes = {
  member: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  churchId: PropTypes.string,
};

export default function useAttendanceData() {
  const { church } = useAuth();
  const [memberData, setMemberData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      {
        Header: "Phone",
        accessor: "phone",
        align: "left",
        width: "15%",
        Cell: PhoneCell,
      },
      { Header: "Email", accessor: "email", align: "left", width: "15%", Cell: EmailCell },
      {
        Header: "Status",
        accessor: "isPresent",
        align: "center",
        width: "10%",
        Cell: AttendanceStatusCell,
      },
      {
        Header: "Checked In At",
        accessor: "checkedInAt",
        align: "left",
        width: "15%",
        Cell: DateTimeCell,
      },
      {
        Header: "Method",
        accessor: "method",
        align: "center",
        width: "10%",
        Cell: MethodCell,
      },
    ],
    rows: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("daily");
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });
  const [totalMembers, setTotalMembers] = useState(0);
  const [presentCount, setPresentCount] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberDetails, setShowMemberDetails] = useState(false);

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setShowMemberDetails(true);
  };

  const closeMemberDetails = () => {
    setShowMemberDetails(false);
    setSelectedMember(null);
  };

  // Create a MemberCell component with the click handler
  const MemberCellWithHandler = ({ value, row }) => (
    <MemberCell value={value} row={row} onClick={handleMemberClick} />
  );
  MemberCellWithHandler.propTypes = {
    value: PropTypes.string.isRequired,
    row: PropTypes.shape({
      original: PropTypes.shape({
        nationality: PropTypes.string,
      }),
    }).isRequired,
  };

  // Update columns to include the Member column with the click handler
  const columnsWithMember = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    {
      Header: "Member",
      accessor: "memberName",
      align: "left",
      width: "25%",
      Cell: MemberCellWithHandler,
    },
    ...memberData.columns.slice(1), // Include all other columns except the first one (SN)
  ];

  const getReportTitle = () => {
    if (!dateRange.startDate) return "Attendance Report";

    if (dateRange.endDate && !isSameDay(dateRange.startDate, dateRange.endDate)) {
      return `Attendance Report (${format(dateRange.startDate, "MMM d, yyyy")} - ${format(
        dateRange.endDate,
        "MMM d, yyyy"
      )})`;
    }

    return `Attendance Report - ${format(dateRange.startDate, "MMM d, yyyy")}`;
  };

  const fetchAttendanceData = async (startDate, endDate) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!church?.id) {
        throw new Error("Church context not available");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      // First, fetch all members
      const membersResponse = await fetch(
        `https://api.ofi-cia.com/api/members/church/${church.id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!membersResponse.ok) {
        throw new Error("Failed to fetch members data");
      }

      const allMembers = await membersResponse.json();
      setTotalMembers(allMembers.length);

      // Then fetch attendance for the date range
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append("start_date", format(startDate, "yyyy-MM-dd"));
        params.append("end_date", format(endDate, "yyyy-MM-dd"));
      }

      const attendanceResponse = await fetch(
        `https://api.ofi-cia.com/api/attendance/report/${church.id}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!attendanceResponse.ok) {
        throw new Error("Failed to fetch attendance data");
      }

      const attendanceData = await attendanceResponse.json();
      const presentMembers = attendanceData || [];

      // Create a map of present member IDs for quick lookup
      const presentMemberIds = new Set();
      const presentMemberRecords = {};

      presentMembers.forEach((record) => {
        if (record.member_id && record.checked_in_at) {
          presentMemberIds.add(record.member_id);
          presentMemberRecords[record.member_id] = record;
        }
      });

      setPresentCount(presentMemberIds.size);

      // Combine all members with attendance data
      const rows = allMembers.map((member, index) => {
        const isPresent = presentMemberIds.has(member.id);
        const attendanceRecord = presentMemberRecords[member.id];

        return {
          id: member.id,
          serial: index + 1,
          memberName:
            member.member_name ||
            `${member.first_name || ""} ${member.surname || ""}`.trim() ||
            "Unknown Member",
          isPresent,
          checkedInAt: isPresent
            ? attendanceRecord.checked_in_at ||
              attendanceRecord.check_time ||
              attendanceRecord.timestamp ||
              "Present"
            : "Absent",
          method: isPresent ? attendanceRecord.method || "biometric" : "N/A",
          phone: member.phone || null,
          email: member.email || null,
          originalData: isPresent ? attendanceRecord : member,
        };
      });

      setMemberData((prev) => ({ ...prev, rows }));
      setDateRange({
        startDate: startDate || startOfDay(new Date()),
        endDate: endDate || endOfDay(new Date()),
      });
    } catch (error) {
      console.error("Failed to fetch attendance data:", error);
      setError(error.message);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load attendance data",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);

    const today = new Date();
    let startDate, endDate;

    switch (newPeriod) {
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate: endOfYear(today);
        break;
      default:
        startDate = null;
        endDate = null;
    }

    if (startDate && endDate) {
      fetchAttendanceData(startDate, endDate);
    } else {
      fetchAttendanceData(null, null);
    }
  };

  const handleCustomDateChange = (start, end) => {
    setPeriod("custom");
    setDateRange({
      startDate: start,
      endDate: end,
    });
    fetchAttendanceData(start, end);
  };

  const exportToCSV = () => {
    const headers = columnsWithMember.map((col) => col.Header);
    const data = memberData.rows.map((row) => {
      return columnsWithMember.map((col) => {
        if (col.accessor === "isPresent") {
          return row[col.accessor] ? "Present" : "Absent";
        }
        return row[col.accessor] || "";
      });
    });

    const csvContent = [headers.join(","), ...data.map((row) => row.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `attendance_report_${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const title = getReportTitle();

    // Title
    doc.setFontSize(16);
    doc.text(title, 14, 15);

    // Church name
    if (church?.name) {
      doc.setFontSize(12);
      doc.text(church.name, 14, 22);
    }

    // Report details
    doc.setFontSize(10);
    doc.text(
      `Period: ${dateRange.startDate ? format(dateRange.startDate, "MMM d, yyyy") : "All time"} ${
        dateRange.endDate && !isSameDay(dateRange.startDate, dateRange.endDate)
          ? ` - ${format(dateRange.endDate, "MMM d, yyyy")}`
          : ""
      }`,
      14,
      30
    );
    doc.text(`Total Members: ${totalMembers}`, 14, 35);
    doc.text(`Present: ${presentCount} | Absent: ${totalMembers - presentCount}`, 14, 40);

    const headers = columnsWithMember.map((col) => col.Header);
    const data = memberData.rows.map((row) => {
      return columnsWithMember.map((col) => {
        if (col.accessor === "isPresent") {
          return row[col.accessor] ? "Present" : "Absent";
        }
        return row[col.accessor] || "";
      });
    });

    doc.autoTable({
      head: [headers],
      body: data,
      startY: 45,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "left",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "center" }, // SN column
        4: { halign: "center" }, // Status column
        6: { halign: "center" }, // Method column
      },
    });

    // Footer
    doc.setFontSize(8);
    doc.text(
      `Generated on ${format(new Date(), "MMM d, yyyy h:mm a")}`,
      14,
      doc.internal.pageSize.height - 10
    );

    doc.save(`attendance_report_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  const printReport = () => {
    const printWindow = window.open("", "_blank");
    const title = getReportTitle();

    const htmlContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .church-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .report-title {
              font-size: 16px;
              margin-bottom: 10px;
            }
            .report-details {
              margin-bottom: 15px;
              font-size: 14px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 10px;
              font-size: 12px;
            }
            th { 
              background-color: #2980b9; 
              color: white; 
              text-align: left; 
              padding: 8px;
              border: 1px solid #ddd;
            }
            td { 
              padding: 8px; 
              border: 1px solid #ddd;
            }
            .center-cell {
              text-align: center;
            }
            tr:nth-child(even) { 
              background-color: #f2f2f2; 
            }
            .footer {
              margin-top: 20px;
              font-size: 11px;
              text-align: right,
              color: #666;
            }
            @media print {
              body { 
                margin: 0; 
                padding: 10px;
              }
              .no-print { 
                display: none; 
              }
              table { 
                width: 100% !important; 
              }
              th, td { 
                padding: 6px !important; 
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${church?.name ? `<div class="church-name">${church.name}</div>` : ""}
            <div class="report-title">${title}</div>
            <div class="report-details">
              <div><strong>Period:</strong> ${
                dateRange.startDate ? format(dateRange.startDate, "MMM d, yyyy") : "All time"
              } ${
      dateRange.endDate && !isSameDay(dateRange.startDate, dateRange.endDate)
        ? ` - ${format(dateRange.endDate, "MMM d, yyyy")}`
        : ""
    }</div>
              <div><strong>Total Members:</strong> ${totalMembers}</div>
              <div><strong>Present:</strong> ${presentCount} | <strong>Absent:</strong> ${
      totalMembers - presentCount
    }</div>
            </div>
          </div>
          
          <button class="no-print" onclick="window.print()" style="margin-bottom: 10px; padding: 5px 10px; background: #2980b9; color: white; border: none; border-radius: 3px; cursor: pointer;">Print Report</button>
          <button class="no-print" onclick="window.close()" style="margin-bottom: 10px; padding: 5px 10px; background: ' #ccc'; color: #333; border: none; border-radius: 3px; cursor: pointer;">Close</button>
          
          <table>
            <thead>
              <tr>
                ${columnsWithMember.map((col) => `<th>${col.Header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${memberData.rows
                .map(
                  (row) => `
                <tr>
                  <td class="center-cell">${row.serial}</td>
                  <td>${row.memberName}</td>
                  <td>${row.phone || "N/A"}</td>
                  <td>${row.email || "N/A"}</td>
                  <td class="center-cell">${row.isPresent ? "Present" : "Absent"}</td>
                  <td>${row.checkedInAt}</td>
                  <td class="center-cell">${row.method}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <div class="footer no-print">
            Generated on ${format(new Date(), "MMM d, yyyy h:mm a")}
          </div>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExport = (type) => {
    switch (type) {
      case "csv":
        exportToCSV();
        break;
      case "pdf":
        exportToPDF();
        break;
      case "print":
        printReport();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (church?.id) {
      fetchAttendanceData(dateRange.startDate, dateRange.endDate);
    }
  }, [church?.id]);

  return {
    columns: columnsWithMember,
    rows: memberData.rows,
    isLoading,
    error,
    period,
    dateRange,
    totalMembers,
    presentCount,
    handlePeriodChange,
    handleCustomDateChange,
    handleExport,
    fetchAttendanceData,
    getReportTitle,
    setDateRange,
    selectedMember,
    showMemberDetails,
    closeMemberDetails,
    churchId: church?.id,
    MemberDetailsModal,
  };
}
